import { useAtomValue } from 'jotai';

import type { TTreatmentType } from '@/react-query/insights/types';
import { usePreferences } from '@/react-query/usePreferences';

import { isCountsAtom, txTypeAtom } from '../../atoms';
import { useGetFilteredClinicalData } from '../data/getClinicalData';

export const useOverallCountsByTx = ({
  practices,
  providers,
}: {
  practices?: string[];
  providers?: string[];
}) => {
  const selectedTreatments = useAtomValue(txTypeAtom);
  const isCounts = useAtomValue(isCountsAtom);

  const { data, isLoading } = useGetFilteredClinicalData({
    // If we're only showing one provider, we don't want to filter their data by materiality
    isMaterialityEnabled: (providers?.length ?? 0) !== 1,
  });

  const { data: prefs } = usePreferences();
  const enabledTreatments = prefs?.insightsEnabledRecos;

  // List of treatments to show
  const treatments = enabledTreatments
    ?.filter(
      (treatment) =>
        !selectedTreatments.length || selectedTreatments?.includes(treatment),
    )
    ?.reduce((acc, treatment) => {
      if (!acc.includes(treatment)) {
        acc.push(treatment);
      }
      return acc;
    }, [] as TTreatmentType[]);

  const filteredProviders = data?.practices?.flatMap((practice) => {
    return practice?.providers
      ?.filter(
        (provider) =>
          (!practices?.length ||
            (practice?.practiceId &&
              practices.includes(practice?.practiceVideaId))) &&
          (!providers?.length ||
            (provider?.providerId &&
              providers.includes(
                provider?.providerVideaId ?? provider?.providerId.toString(),
              ))),
      )
      .map((provider) => {
        return {
          ...provider,
          estimatedTreatmentValues: practice?.estimatedTreatmentValues,
        };
      });
  });

  const treatmentOpportunity = treatments
    ?.map((tx) => {
      const providerCounts = filteredProviders?.map((provider) => {
        const treatmentCost = isCounts
          ? 1
          : (provider?.estimatedTreatmentValues?.[tx]?.value ?? 0);

        const recommendation =
          ((provider?.recommendations?.[tx]?.current?.planned?.treatmentCount ??
            0) +
            (provider?.recommendations?.[tx]?.current?.unplanned
              ?.treatmentCount ?? 0)) *
          treatmentCost;

        const production = isCounts
          ? (provider?.production?.[tx]?.current?.count ?? 0)
          : (provider?.production?.[tx]?.current?.value ?? 0);

        return {
          recommendation,
          production,
        };
      });
      const opportunity = providerCounts?.reduce((acc, provider) => {
        return acc + (provider?.recommendation ?? 0);
      }, 0);
      const completed = providerCounts?.reduce((acc, provider) => {
        return acc + (provider?.production ?? 0);
      }, 0);
      return { tx, opportunity: opportunity ?? 0, completed: completed ?? 0 };
    })
    .filter((tx) => tx?.opportunity > 0 || tx?.completed > 0);

  return { treatmentOpportunity, isLoading };
};
